import React, { useMemo, useState } from "react";
import { Box, Button, FormControl, FormHelperText, Input, Sheet, Stack, Typography } from "@mui/joy";
import FullPageContainer from "../container/FullPageContainer";
import { theme } from "../../styles/theme";
import BackButton from "../../components/common/BackButton";
import { useNavigate } from "react-router-dom";
import useGetDeviceInfo from "../../hooks/useGetDeviceInfo";
import { compareVersions } from "../../logic/logics_common";
import useIsInappWebview from "../../hooks/useIsInappWebview";
import { useSetRecoilState } from "recoil";
import { userAtom, type UserType } from "../../recoil/userAtom";
import { patientAtom } from "../../recoil/patientAtom";
import { setAuthHeaders, type CustomResponseFormat } from "api";
import { userApi } from "../../api/userApi";

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function EmailLoginPage() {
  const navigate = useNavigate();
  const { homeIndicatorHeight, appVersion } = useGetDeviceInfo();
  const isInapp = useIsInappWebview();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAllowed = useMemo(
    () => (!isInapp || compareVersions(appVersion, "1.1.52") >= 0),
    [appVersion, isInapp]
  );

  const setUser = useSetRecoilState(userAtom);
  const setPatient = useSetRecoilState(patientAtom);

  // Temporary demo credentials restriction
  const DEMO_EMAIL = "distancingdemo1@gmail.com";
  const DEMO_PASSWORD = "demo123!@#";

  const canSubmit =
    email.trim().toLowerCase() === DEMO_EMAIL &&
    password === DEMO_PASSWORD &&
    !submitting;

  const onBack = () => navigate(-1);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      // Build idToken as "email:password" and send as type "other"
      const idToken = `${email.trim()}:${password}`;
      const res = (await userApi.login({
        type: "other",
        idToken,
      })) as unknown as CustomResponseFormat<UserType> & { result?: any };

      const result: any = (res as any)?.result ?? (res as any)?.data ?? res;

      if (result?.accessToken) {
        setAuthHeaders(result.accessToken);
      }
      setUser(result as UserType);
      if (result?.role === "patient" && result?.userId) {
        setPatient({
          patientId: Number(`${result.userId}`),
          nickname: result.nickname,
        });
      }
      navigate("/", { replace: true });
    } catch (e: any) {
      setError("아이디 또는 비밀번호를 확인해 주세요.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FullPageContainer hasFooter={false} backgroundColor={theme.vars.palette.Background.Level2}>
      {/* Header with back button */}
      <Sheet
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "56px",
          display: "flex",
          alignItems: "center",
          px: 1.5,
          backgroundColor: theme.vars.palette.Background.Level2,
          zIndex: 10,
          borderBottom: 1,
          borderColor: "background.level3",
        }}
      >
        <BackButton onBack={onBack} />
        <Typography level="title-sm" sx={{ ml: 0.5 }}>
          이메일로 로그인
        </Typography>
      </Sheet>

      {/* Content */}
      <Stack sx={{ pt: "72px", px: 2, gap: 2 }}>
        {!isAllowed && (
          <Typography level="body-sm" color="danger">
            최신 앱 버전에서만 사용할 수 있어요. 앱을 업데이트해 주세요.
          </Typography>
        )}

        <FormControl>
          <Typography level="body-sm" sx={{ mb: 0.5 }}>
            이메일
          </Typography>
          <Input
            type="email"
            size="lg"
            sx={{ "--Input-minHeight": "46px", "--Input-paddingInline": "16px" }}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="example@email.com"
            inputMode="email"
          />
          {email.length > 0 && !isValidEmail(email) && (
            <FormHelperText color="danger">올바른 이메일 형식이 아니에요.</FormHelperText>
          )}
        </FormControl>

        <FormControl>
          <Typography level="body-sm" sx={{ mb: 0.5 }}>
            비밀번호
          </Typography>
          <Input
            type="password"
            size="lg"
            sx={{ "--Input-minHeight": "46px", "--Input-paddingInline": "16px" }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="비밀번호"
          />
        </FormControl>

        {error && (
          <Typography level="body-sm" color="danger">
            {error}
          </Typography>
        )}
      </Stack>

      {/* Bottom fixed login button */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          pb: `${homeIndicatorHeight}px`,
          backgroundColor: theme.vars.palette.Background.Level2,
          borderTop: 1,
          borderColor: "background.level3",
        }}
      >
        <Stack sx={{ p: 2 }}>
          <Button
            size="lg"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!isAllowed || !canSubmit}
          >
            로그인
          </Button>
        </Stack>
      </Box>
    </FullPageContainer>
  );
}
